/**
 * ItemTypeSelector - Generic item type dropdown with gear icon
 *
 * Reusable component for List, Slider, ProgressBar sections.
 * Shows dropdown for item type selection and gear icon for style settings toggle.
 *
 * Features:
 * - Data loss confirmation dialog when changing types
 * - Configurable item type options
 * - Optional styles toggle button (gear icon)
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { ItemTypeChangeDialog, FieldsToClear } from './ItemTypeChangeDialog';
import { FIELD_MARGIN_BOTTOM } from '../../../../config/ui';
import {
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../../../config/editorFieldStyles';

/**
 * Item type option structure
 */
export interface ItemTypeOption {
  value: string;
  label: string;
}

/**
 * Generic visibility configuration for item fields
 * Each section type can define which fields are visible
 */
export interface ItemVisibilityConfig {
  showImage?: boolean;
  showTitle?: boolean;
  showSubtitle?: boolean;
  showSubtitle1?: boolean;
  showSubtitle2?: boolean;
  showDescription?: boolean;
  showCheckbox?: boolean;
  [key: string]: boolean | undefined; // Allow additional fields
}

/**
 * Props for ItemTypeSelector
 */
export interface ItemTypeSelectorProps {
  /** Current item type value */
  value: string;

  /** Available item type options */
  options: ItemTypeOption[];

  /** Function to get visibility config for an item type */
  getVisibility: (itemType: string) => ItemVisibilityConfig;

  /** Callback when item type changes (after confirmation if needed) */
  onChange: (newItemType: string) => void;

  /** Callback when gear icon clicked to expand styles */
  onStylesClick?: () => void;

  /** Whether control is disabled */
  disabled?: boolean;

  /** Whether styles panel is expanded */
  stylesExpanded?: boolean;

  /** Test ID prefix for components */
  testIdPrefix?: string;

  /** Label for the select field */
  label?: string;
}

/**
 * Determine which fields will be cleared when changing item types
 */
function getFieldsToClear(
  fromVisibility: ItemVisibilityConfig,
  toVisibility: ItemVisibilityConfig
): FieldsToClear {
  return {
    clearSubtitle: Boolean(
      (fromVisibility.showSubtitle && !toVisibility.showSubtitle) ||
        (fromVisibility.showSubtitle1 && !toVisibility.showSubtitle1) ||
        (fromVisibility.showSubtitle2 && !toVisibility.showSubtitle2)
    ),
    clearImage: Boolean(fromVisibility.showImage && !toVisibility.showImage),
    clearCheckbox: Boolean(fromVisibility.showCheckbox && !toVisibility.showCheckbox),
    clearDescription: Boolean(fromVisibility.showDescription && !toVisibility.showDescription),
    clearTitle: Boolean(fromVisibility.showTitle && !toVisibility.showTitle),
  };
}

/**
 * Check if any data will be lost
 */
function willLoseData(fieldsToClear: FieldsToClear): boolean {
  return Object.values(fieldsToClear).some(Boolean);
}

/**
 * ItemTypeSelector - Generic item type dropdown with optional gear icon
 */
export const ItemTypeSelector: React.FC<ItemTypeSelectorProps> = ({
  value,
  options,
  getVisibility,
  onChange,
  onStylesClick,
  disabled = false,
  stylesExpanded = false,
  testIdPrefix = 'item',
  label = 'Item type',
}) => {
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingItemType, setPendingItemType] = useState<string | null>(null);

  // Get visibility config for current value
  const currentVisibility = useMemo(() => getVisibility(value), [getVisibility, value]);

  // Find label for a value
  const getLabelForValue = useCallback(
    (itemTypeValue: string): string => {
      const option = options.find((opt) => opt.value === itemTypeValue);
      return option?.label || itemTypeValue;
    },
    [options]
  );

  // Handle select change
  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value;

      if (newValue === value) {
        return;
      }

      // Check if this will cause data loss
      const newVisibility = getVisibility(newValue);
      const fieldsToClear = getFieldsToClear(currentVisibility, newVisibility);

      if (willLoseData(fieldsToClear)) {
        // Show confirmation dialog
        setPendingItemType(newValue);
        setDialogOpen(true);
      } else {
        // Safe to change directly
        onChange(newValue);
      }
    },
    [value, getVisibility, currentVisibility, onChange]
  );

  // Handle dialog confirm
  const handleDialogConfirm = useCallback(() => {
    if (pendingItemType) {
      onChange(pendingItemType);
    }
    setDialogOpen(false);
    setPendingItemType(null);
  }, [pendingItemType, onChange]);

  // Handle dialog cancel
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setPendingItemType(null);
  }, []);

  // Compute fields to clear for dialog
  const fieldsToClear = useMemo(() => {
    if (!pendingItemType) {
      return {
        clearSubtitle: false,
        clearImage: false,
        clearCheckbox: false,
        clearDescription: false,
        clearTitle: false,
      };
    }
    const newVisibility = getVisibility(pendingItemType);
    return getFieldsToClear(currentVisibility, newVisibility);
  }, [pendingItemType, getVisibility, currentVisibility]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: FIELD_MARGIN_BOTTOM,
        }}
      >
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id={`${testIdPrefix}-type-select-label`}>{label}</InputLabel>
          <Select
            labelId={`${testIdPrefix}-type-select-label`}
            id={`${testIdPrefix}-type-select`}
            value={value}
            label={label}
            onChange={handleSelectChange}
            disabled={disabled}
            data-testid={`${testIdPrefix}-type-selector`}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {onStylesClick && (
          <IconButton
            onClick={onStylesClick}
            disabled={disabled}
            size="small"
            data-testid={`${testIdPrefix}-styles-gear`}
            sx={stylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <ItemTypeChangeDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        fieldsToClear={fieldsToClear}
        fromType={getLabelForValue(value)}
        toType={pendingItemType ? getLabelForValue(pendingItemType) : ''}
      />
    </>
  );
};

export default ItemTypeSelector;
